import React, { useState, useEffect } from 'react';
import { database, ref, get, update } from '../../firebase';
import { Award, Users, DollarSign, Gift, Target, CheckCircle, Trophy, Layers, AlertCircle } from 'lucide-react';

// Dialog Component (same as before)
const Dialog = ({ isOpen, onClose, title, message, type = 'info' }) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-card border border-border rounded-xl p-5 max-w-sm w-full shadow-2xl">
        <div className="text-center">
          <div className="mb-3">
            {type === 'success' ? (
              <CheckCircle className="w-10 h-10 text-green-500 mx-auto" />
            ) : type === 'error' ? (
              <AlertCircle className="w-10 h-10 text-red-500 mx-auto" />
            ) : (
              <Award className="w-10 h-10 text-blue-500 mx-auto" />
            )}
          </div>
          
          <h3 className="text-base font-bold text-foreground mb-1.5">{title}</h3>
          <p className="text-xs text-muted-foreground mb-4">{message}</p>
          
          <button
            onClick={onClose}
            className="btn-electric px-5 py-2 rounded-lg w-full text-xs"
          >
            OK
          </button>
        </div>
      </div>
    </div>
  );
};

const TeamSalesRewards = () => {
  const [loading, setLoading] = useState(true);
  const [userData, setUserData] = useState(null);
  const [teamPackages, setTeamPackages] = useState([]);
  const [allRewards, setAllRewards] = useState([]);
  const [claimedRewardIds, setClaimedRewardIds] = useState(new Set());
  const [totalRewardsEarned, setTotalRewardsEarned] = useState(0);
  const [claimingRewardId, setClaimingRewardId] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [showLevelBreakdown, setShowLevelBreakdown] = useState(false);
  const [levelSales, setLevelSales] = useState([]);
  
  // Dialog state
  const [dialog, setDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    type: 'info'
  });

  // Get user info
  const userId = localStorage.getItem('userId') || '';
  const userName = localStorage.getItem('userName') || '';
  const userSponsorId = localStorage.getItem('userSponsorId') || userId;
  const userDbKey = localStorage.getItem('dbKey') || '';
  const registrationDate = localStorage.getItem('registrationDate') || '';

  useEffect(() => {
    fetchAllData();
  }, []);

  const showDialog = (title, message, type = 'info') => {
    setDialog({
      isOpen: true,
      title,
      message,
      type
    });
  };

  const closeDialog = () => {
    setDialog({
      isOpen: false,
      title: '',
      message: '',
      type: 'info'
    });
  };

  const fetchAllData = async () => {
    try {
      setLoading(true);
      await Promise.all([
        fetchUserData(),
        fetchTeamRewards(),
        fetchClaimedRewards()
      ]);
    } catch (error) {
      console.error('Error fetching data:', error);
      showDialog('Error', 'Failed to load team sales data', 'error');
    } finally {
      setLoading(false);
    }
  };

  const fetchUserData = async () => {
    try {
      if (!userDbKey || !registrationDate) {
        showDialog('Session Error', 'Please login again', 'error');
        return;
      }

      const userRef = ref(database, `users/${registrationDate}/${userDbKey}`);
      const snapshot = await get(userRef);
      
      if (snapshot.exists()) {
        const user = snapshot.val();
        setUserData(user);
        await calculateTeamSales(userSponsorId);
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      showDialog('Error', 'Failed to load user data', 'error');
    }
  };

  // IMPROVED: Check if user has active packages - check multiple possible structures
  const hasActivePackages = (user) => {
    if (!user) return false;
    
    // Check different possible package structures
    const packages = user.packages || [];
    
    // If packages is an array
    if (Array.isArray(packages)) {
      return packages.some(pkg => {
        // Check for active status in different possible field names
        const status = pkg.status || pkg.packageStatus || pkg.state;
        return status === 'active' || pkg.isActive === true;
      });
    }
    
    // If packages is an object
    if (typeof packages === 'object') {
      return Object.values(packages).some(pkg => {
        if (typeof pkg === 'object') {
          const status = pkg.status || pkg.packageStatus || pkg.state;
          return status === 'active' || pkg.isActive === true;
        }
        return false;
      });
    }
    
    return false;
  };

  // Get user activation date
  const getUserActivationDate = (user) => {
    if (!user || !user.packages) return null;
    
    const packages = user.packages || [];
    let earliestDate = null;
    
    // Find earliest purchase date among active packages
    if (Array.isArray(packages)) {
      packages.forEach(pkg => {
        if (pkg.status === 'active' || pkg.packageStatus === 'active') {
          const purchaseDate = new Date(pkg.purchaseDate || pkg.activationDate || pkg.date);
          if (!isNaN(purchaseDate.getTime())) {
            if (!earliestDate || purchaseDate < earliestDate) {
              earliestDate = purchaseDate;
            }
          }
        }
      });
    }
    
    return earliestDate;
  };

  // IMPROVED: Calculate team sales - same logic as LeaderRewards
  const calculateTeamSales = async (sponsorId) => {
    try {
      const usersRef = ref(database, 'users');
      const snapshot = await get(usersRef);
      
      if (!snapshot.exists()) {
        console.log('No users found in database');
        setTeamPackages([]);
        setLevelSales(Array(11).fill({ sales: 0, members: 0 }));
        return;
      }

      const usersByDate = snapshot.val();
      const allPackages = [];
      const levelData = Array(11).fill().map(() => ({ sales: 0, members: new Set() }));
      
      // Get current user
      let currentUser = null;
      for (const date in usersByDate) {
        for (const [dbKey, user] of Object.entries(usersByDate[date])) {
          if (user.sponsorId === userSponsorId) {
            currentUser = user;
            break;
          }
        }
        if (currentUser) break;
      }
      
      if (!currentUser) {
        console.log('Current user not found');
        setTeamPackages([]);
        setLevelSales(levelData.map(ld => ({ sales: ld.sales, members: 0 })));
        return;
      }
      
      // Check if current user is active
      if (!hasActivePackages(currentUser)) {
        console.log('Current user is INACTIVE - cannot earn team sales rewards');
        setTeamPackages([]);
        setLevelSales(levelData.map(ld => ({ sales: ld.sales, members: 0 })));
        return;
      }
      
      const uplineActivationDate = getUserActivationDate(currentUser);
      console.log('Upline activation date:', uplineActivationDate);
      
      // Recursive function to find downlines (SAME as LeaderRewards)
      const findDownlinePackages = (currentSponsorId, level = 1) => {
        if (level > 10) return;
        
        for (const date in usersByDate) {
          const users = usersByDate[date];
          
          for (const [dbKey, user] of Object.entries(users)) {
            if (user.uplineSponsorId === currentSponsorId) {
              console.log(`Found downline at Level ${level}:`, user.sponsorId, user.name);
              
              // Check if downline is active
              const isDownlineActive = hasActivePackages(user);
              
              if (isDownlineActive) {
                console.log(`Downline ${user.sponsorId} is ACTIVE`);
                levelData[level].members.add(user.sponsorId);
                
                // Extract packages from downline
                const packages = user.packages || [];
                
                if (Array.isArray(packages)) {
                  packages.forEach(pkg => {
                    const status = pkg.status || pkg.packageStatus;
                    if (status === 'active') {
                      const amount = parseFloat(pkg.totalAmount) || parseFloat(pkg.baseAmount) || 0;
                      const purchaseDate = new Date(pkg.purchaseDate || pkg.activationDate);
                      
                      if (!isNaN(purchaseDate.getTime())) {
                        // Check if purchased after upline activation
                        if (!uplineActivationDate || purchaseDate > uplineActivationDate) {
                          allPackages.push({
                            memberId: user.sponsorId,
                            memberName: user.name || 'Unknown',
                            packageName: pkg.packageName || 'Package',
                            totalAmount: amount,
                            level: level,
                            purchaseDate: purchaseDate.toISOString()
                          });
                          
                          levelData[level].sales += amount;
                          console.log(`Added package: $${amount} from ${user.sponsorId}`);
                        } else {
                          console.log(`Package skipped: Purchased before upline activation`);
                        }
                      }
                    }
                  });
                }
              } else {
                console.log(`Downline ${user.sponsorId} is INACTIVE - skipping packages`);
              }
              
              // Recursively check next level
              findDownlinePackages(user.sponsorId, level + 1);
            }
          }
        }
      };

      console.log('Starting team sales calculation from sponsor:', sponsorId);
      findDownlinePackages(sponsorId, 1);
      
      // Format level data
      const formattedLevelSales = levelData.map((data, index) => ({
        sales: data.sales,
        members: data.members.size,
        level: index
      }));
      
      console.log('Team sales calculation complete:');
      console.log('- Total packages found:', allPackages.length);
      console.log('- Total sales:', allPackages.reduce((sum, pkg) => sum + pkg.totalAmount, 0));
      console.log('- Level distribution:', formattedLevelSales);
      
      setTeamPackages(allPackages);
      setLevelSales(formattedLevelSales);
      
    } catch (error) {
      console.error('Error calculating team sales:', error);
      setTeamPackages([]);
      setLevelSales(Array(11).fill({ sales: 0, members: 0 }));
    }
  };

  const fetchTeamRewards = async () => {
    try {
      const rewardsRef = ref(database, 'rewardSettings/teamsales');
      const snapshot = await get(rewardsRef);
      
      if (snapshot.exists()) {
        const rewardsData = snapshot.val();
        const rewardsArray = Array.isArray(rewardsData)
          ? rewardsData
          : Object.entries(rewardsData || {}).map(([key, reward]) => ({
              ...reward,
              _key: key
            }));

        const processedRewards = rewardsArray
          .filter(reward => reward && reward.status !== 'inactive')
          .map((reward, index) => ({
            ...reward,
            id: reward.id || reward._key || `TSR${index + 1}`,
            salesTarget: reward.requirement?.salesTarget || reward.salesTarget || 0,
            cashAmount: reward.reward?.amount || 0,
            giftValue: reward.reward?.giftValue || 0
          }))
          .sort((a, b) => a.salesTarget - b.salesTarget);

        console.log('Loaded team sales rewards:', processedRewards.length);
        setAllRewards(processedRewards);
      } else {
        console.log('No team sales rewards found in database');
        setAllRewards([]);
      }
    } catch (error) {
      console.error('Error fetching team rewards:', error);
      setAllRewards([]);
    }
  };

  const fetchClaimedRewards = async () => {
    try {
      if (!userDbKey || !registrationDate) return;
      
      const userRef = ref(database, `users/${registrationDate}/${userDbKey}`);
      const snapshot = await get(userRef);
      
      const claimedIds = new Set();
      let totalEarned = 0;
      
      if (snapshot.exists()) {
        const user = snapshot.val();
        
        if (user.teamRewards && Array.isArray(user.teamRewards)) {
          user.teamRewards.forEach(reward => {
            if (reward.rewardId && reward.status === 'claimed') {
              claimedIds.add(reward.rewardId);
              totalEarned += parseFloat(reward.cashAmount) || 0;
            }
          });
          console.log('Found claimed team rewards:', claimedIds.size);
        }
      }
      
      setClaimedRewardIds(claimedIds);
      setTotalRewardsEarned(totalEarned);
    } catch (error) {
      console.error('Error fetching claimed rewards:', error);
    }
  };

  // Calculate total team sales
  const calculateTotalTeamSales = () => {
    const total = teamPackages.reduce((sum, pkg) => sum + (parseFloat(pkg.totalAmount) || 0), 0);
    console.log('Current team sales total:', total);
    return total;
  };

// FIXED: Calculate reward progress - SEQUENTIAL with cumulative tracking
const calculateRewardProgress = (rewards) => {
  const totalTeamSales = calculateTotalTeamSales();
  console.log('Calculating progress for total team sales:', totalTeamSales);
  
  let claimedTargets = 0; // Track cumulative claimed targets
  let previousClaimed = true;
  
  return rewards.map((reward, index) => {
    const target = reward.salesTarget || 0;
    const isClaimed = claimedRewardIds.has(reward.id);
    
    let currentSales = 0;
    let progress = 0;
    let status = 'locked';
    let lockReason = null;
    
    if (isClaimed) {
      currentSales = target;
      progress = 100;
      status = 'claimed';
      claimedTargets += target; // Add to cumulative claimed targets
      previousClaimed = true;
    } 
    else if (previousClaimed) {
      // Check if previous reward is claimed
      const previousReward = index > 0 ? rewards[index - 1] : null;
      const isPreviousClaimed = previousReward ? claimedRewardIds.has(previousReward.id) : true;
      
      if (isPreviousClaimed) {
        // Calculate available sales (total - all claimed targets)
        const availableSales = Math.max(0, totalTeamSales - claimedTargets);
        currentSales = Math.min(availableSales, target);
        progress = target > 0 ? (currentSales / target) * 100 : 0;
        
        // Check if eligible (need enough TOTAL sales for cumulative target)
        const cumulativeTargetNeeded = claimedTargets + target;
        
        if (totalTeamSales >= cumulativeTargetNeeded && currentSales >= target) {
          status = 'eligible';
        } else {
          status = 'in_progress';
        }
        
        previousClaimed = status === 'eligible';
      } else {
        // Previous reward NOT claimed - this reward is locked
        status = 'locked';
        lockReason = `Complete Reward ${index} first`;
        currentSales = 0;
        progress = 0;
        previousClaimed = false;
      }
    } 
    else {
      status = 'locked';
      lockReason = `Complete Reward ${index} first`;
    }
    
    console.log(`Reward ${index + 1}: ${status}, Sales: ${currentSales}/${target}, Progress: ${progress}%`);
    
    return {
      ...reward,
      currentSales,
      progress: Math.min(100, progress),
      status,
      isEligible: status === 'eligible',
      isClaimed,
      lockReason
    };
  });
};

  const getFilteredRewards = () => {
    const progressRewards = calculateRewardProgress(allRewards);
    
    if (activeTab === 'available') {
      return progressRewards.filter(reward => reward.status === 'eligible');
    } else if (activeTab === 'claimed') {
      return progressRewards.filter(reward => reward.status === 'claimed');
    } else if (activeTab === 'locked') {
      return progressRewards.filter(reward => reward.status === 'locked');
    }
    
    return progressRewards;
  };

  const claimReward = async (reward) => {
    try {
      if (!userDbKey || !registrationDate) {
        showDialog('Session Error', 'Please login again', 'error');
        return;
      }

      if (claimingRewardId === reward.id) return;
      
      setClaimingRewardId(reward.id);
      
      if (!reward.isEligible) {
        const needed = (reward.salesTarget || 0) - reward.currentSales;
        showDialog('Not Ready', `You need $${needed} more in team sales to claim this reward.`, 'info');
        setClaimingRewardId(null);
        return;
      }
      
      const timestamp = new Date().toISOString();
      const cashAmount = reward.cashAmount || 0;
      const giftValue = reward.giftValue || 0;
      const totalTeamSales = calculateTotalTeamSales();
      
      // Update user data
      const userRef = ref(database, `users/${registrationDate}/${userDbKey}`);
      const snapshot = await get(userRef);
      
      if (!snapshot.exists()) {
        showDialog('Error', 'User data not found', 'error');
        setClaimingRewardId(null);
        return;
      }
      
      const userData = snapshot.val();
      const currentWorkingBalance = parseFloat(userData.wallets?.working) || 0;
      const newWorkingBalance = currentWorkingBalance + cashAmount;
      
      const updates = {
        'wallets/working': newWorkingBalance,
        totalEarnings: (parseFloat(userData.totalEarnings) || 0) + cashAmount,
        updatedAt: timestamp
      };
      
      const claimedReward = {
        rewardId: reward.id,
        rewardName: reward.name,
        cashAmount: cashAmount,
        giftValue: giftValue,
        salesTarget: reward.salesTarget || 0,
        teamSales: totalTeamSales,
        claimedAt: timestamp,
        status: 'claimed'
      };
      
      const currentTeamRewards = userData.teamRewards || [];
      updates['teamRewards'] = [...currentTeamRewards, claimedReward];
      
      await update(userRef, updates);
      
      // Update local state
      setClaimedRewardIds(prev => new Set([...prev, reward.id]));
      setTotalRewardsEarned(prev => prev + cashAmount);
      
      // Refresh data
      fetchUserData();
      fetchClaimedRewards();
      
      let message = `Reward claimed! $${cashAmount} added to your Working Wallet.`;
      if (giftValue > 0) {
        message += ` Plus $${giftValue} gift value.`;
      }
      
      showDialog('Success', 'Reward Claimed', message, 'success');
      
    } catch (error) {
      console.error('Error claiming reward:', error);
      showDialog('Error', 'Failed to claim reward. Please try again.', 'error');
    } finally {
      setClaimingRewardId(null);
    }
  };

  const formatNumber = (num) => {
    if (typeof num !== 'number' || isNaN(num)) return '0';
    return num.toLocaleString('en-US', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    });
  };

  const formatDecimal = (num) => {
    if (typeof num !== 'number' || isNaN(num)) return '0.00';
    return num.toFixed(2);
  };

  const getStatusColor = (status) => {
    switch(status) {
      case 'claimed': return { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' };
      case 'eligible': return { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' };
      case 'in_progress': return { bg: 'bg-blue-500/20', text: 'text-blue-400', border: 'border-blue-500/30' };
      case 'locked': return { bg: 'bg-gray-500/20', text: 'text-gray-400', border: 'border-gray-500/30' };
      default: return { bg: 'bg-secondary', text: 'text-muted-foreground', border: 'border-border' };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="px-3 pt-12 pb-4">
          <div className="flex justify-center items-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </div>
      </div>
    );
  }

  const filteredRewards = getFilteredRewards();
  const totalTeamSales = calculateTotalTeamSales();
  const teamMembers = Array.from(new Set(teamPackages.map(pkg => pkg.memberId))).length;

  console.log('Team Sales Stats:', {
    totalTeamSales,
    teamMembers,
    teamPackages: teamPackages.length,
    allRewards: allRewards.length,
    filteredRewards: filteredRewards.length
  });

  return (
    <>
      <Dialog
        isOpen={dialog.isOpen}
        onClose={closeDialog}
        title={dialog.title}
        message={dialog.message}
        type={dialog.type}
      />

      <div className="min-h-screen bg-background">
        <div className="px-3 pt-10 pb-4">
          {/* Header */}
          <div className="mb-4">
            <h1 className="text-base font-semibold text-foreground">Team Sales Rewards</h1>
            <p className="text-xs text-muted-foreground mt-0.5">Earn from your entire team's sales (Levels 1-10)</p>
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-2 gap-2 mb-4">
            <div className="bg-card p-3 rounded-xl border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-muted-foreground mb-0.5">Team Sales</p>
                  <p className="text-sm font-bold text-foreground">${formatDecimal(totalTeamSales)}</p>
                </div>
                <DollarSign className="text-blue-400" size={16} />
              </div>
              <p className="text-[8px] text-muted-foreground mt-1">After activation</p>
            </div>

            <div className="bg-card p-3 rounded-xl border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-muted-foreground mb-0.5">Active Members</p>
                  <p className="text-sm font-bold text-foreground">{teamMembers}</p>
                </div>
                <Users className="text-green-400" size={16} />
              </div>
              <p className="text-[8px] text-muted-foreground mt-1">Levels 1-10</p>
            </div>

            <div className="bg-card p-3 rounded-xl border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-muted-foreground mb-0.5">Rewards Earned</p>
                  <p className="text-sm font-bold text-foreground">${formatDecimal(totalRewardsEarned)}</p>
                </div>
                <Trophy className="text-yellow-400" size={16} />
              </div>
              <p className="text-[8px] text-muted-foreground mt-1">Total claimed</p>
            </div>

            <div className="bg-card p-3 rounded-xl border border-border">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-[10px] text-muted-foreground mb-0.5">Available Now</p>
                  <p className="text-sm font-bold text-foreground">
                    {filteredRewards.filter(r => r.status === 'eligible').length}
                  </p>
                </div>
                <Gift className="text-purple-400" size={16} />
              </div>
              <p className="text-[8px] text-muted-foreground mt-1">Ready to claim</p>
            </div>
          </div>

          {/* Filter Tabs */}
          <div className="mb-4">
            <div className="flex bg-card rounded-lg p-1 border border-border">
              <button
                onClick={() => setActiveTab('all')}
                className={`flex-1 py-2 text-center text-[10px] font-medium rounded-lg transition-colors ${
                  activeTab === 'all' ? 'bg-primary text-white' : 'text-muted-foreground'
                }`}
              >
                All ({allRewards.length})
              </button>
              <button
                onClick={() => setActiveTab('available')}
                className={`flex-1 py-2 text-center text-[10px] font-medium rounded-lg transition-colors ${
                  activeTab === 'available' ? 'bg-yellow-500 text-white' : 'text-muted-foreground'
                }`}
              >
                Available ({filteredRewards.filter(r => r.status === 'eligible').length})
              </button>
              <button
                onClick={() => setActiveTab('claimed')}
                className={`flex-1 py-2 text-center text-[10px] font-medium rounded-lg transition-colors ${
                  activeTab === 'claimed' ? 'bg-green-500 text-white' : 'text-muted-foreground'
                }`}
              >
                Claimed ({claimedRewardIds.size})
              </button>
            </div>
          </div>

          {/* Rewards List - Sequential */}
          <div className="space-y-3">
            {filteredRewards.length === 0 ? (
              <div className="bg-card p-6 rounded-xl border border-border text-center">
                <Award className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
                <h3 className="text-sm font-bold text-foreground mb-1">No Rewards Found</h3>
                <p className="text-[10px] text-muted-foreground">
                  {allRewards.length === 0 
                    ? 'No team sales rewards configured yet.' 
                    : 'No rewards match your current filters.'}
                </p>
              </div>
            ) : (
              <div className="relative">
                {filteredRewards.map((reward, index) => {
                  const colors = getStatusColor(reward.status);
                  const isLast = index === filteredRewards.length - 1;
                  
                  return (
                    <div key={reward.id} className="relative">
                      {/* Connection Line */}
                      {!isLast && (
                        <div className="absolute left-4 top-10 bottom-0 w-0.5 bg-border z-0"></div>
                      )}
                      
                      {/* Reward Card */}
                      <div className={`relative z-10 ml-5 bg-card p-3 border ${colors.border} ${colors.bg} rounded-xl mb-3`}>
                        {/* Step Number */}
                        <div className={`absolute -left-4 top-1/2 -translate-y-1/2 w-7 h-7 rounded-full flex items-center justify-center border-2 ${colors.border} ${colors.bg}`}>
                          {reward.status === 'claimed' ? (
                            <CheckCircle size={12} className="text-green-400" />
                          ) : reward.status === 'locked' ? (
                            <Target size={12} className="text-gray-400" />
                          ) : (
                            <span className={`text-[10px] font-bold ${colors.text}`}>{index + 1}</span>
                          )}
                        </div>
                        
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-1.5 mb-1.5">
                              <h3 className="text-xs font-bold text-foreground">{reward.name}</h3>
                              <span className={`text-[8px] px-1.5 py-0.5 rounded-full ${colors.text} ${colors.border} border`}>
                                {reward.status === 'claimed' ? '✅ Claimed' : 
                                 reward.status === 'eligible' ? '🎁 Ready' : 
                                 reward.status === 'locked' ? '🔒 Locked' : '⏳ Progress'}
                              </span>
                            </div>
                            
                            <div className="flex flex-wrap gap-1 mb-2">
                              <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-secondary text-muted-foreground">
                                Target: ${formatNumber(reward.salesTarget)}
                              </span>
                              <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-blue-500/20 text-blue-400">
                                Levels 1-10
                              </span>
                              {reward.status === 'locked' && index > 0 && (
                                <span className="text-[8px] px-1.5 py-0.5 rounded-full bg-gray-500/20 text-gray-400">
                                  Complete Reward {index} first
                                </span>
                              )}
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <div className="text-xs font-bold text-green-400">
                              ${formatNumber(reward.cashAmount)}
                            </div>
                            {reward.giftValue > 0 && (
                              <div className="text-[8px] text-pink-400">
                                + ${formatNumber(reward.giftValue)} gift
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Progress Bar */}
                        <div className="mt-2">
                          <div className="flex justify-between text-[8px] text-muted-foreground mb-1">
                            <span>Progress: {Math.round(reward.progress)}%</span>
                            <span>
                              ${formatDecimal(reward.currentSales)} / ${formatNumber(reward.salesTarget)}
                            </span>
                          </div>
                          <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                            <div 
                              className={`h-full rounded-full ${
                                reward.status === 'claimed' ? 'bg-green-500' :
                                reward.status === 'eligible' ? 'bg-yellow-500' :
                                reward.status === 'in_progress' ? 'bg-blue-500' :
                                'bg-gray-500'
                              }`}
                              style={{ width: `${reward.progress}%` }}
                            ></div>
                          </div>
                        </div>

                        {/* Action Button */}
                        {reward.status === 'eligible' && !reward.isClaimed && (
                          <div className="mt-3 flex justify-end">
                            <button
                              onClick={() => claimReward(reward)}
                              disabled={claimingRewardId === reward.id}
                              className="btn-electric px-3 py-1.5 rounded-lg text-[10px] font-medium disabled:opacity-70"
                            >
                              {claimingRewardId === reward.id ? (
                                <div className="flex items-center gap-1">
                                  <div className="animate-spin rounded-full h-3 w-3 border-t-2 border-b-2 border-white"></div>
                                  <span>Claiming...</span>
                                </div>
                              ) : (
                                <div className="flex items-center gap-1">
                                  <Gift size={10} />
                                  <span>Claim ${formatNumber(reward.cashAmount)}</span>
                                </div>
                              )}
                            </button>
                          </div>
                        )}
                        
                        {/* Locked Message */}
                        {reward.status === 'locked' && index > 0 && (
                          <div className="mt-2 p-2 bg-gray-500/10 rounded-lg">
                            <div className="flex items-center gap-1 text-gray-400">
                              <Target size={10} />
                              <span className="text-[8px]">
                                Complete <span className="font-bold">Reward {index}</span> first
                              </span>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

        </div>
      </div>
    </>
  );
};

export default TeamSalesRewards;